import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import Cookies from 'js-cookie';
import api, { usersAPI } from '../services/api';
import { resetPulseState } from '../components/pulse/pulseLive';
import { changeSession, announceSignOut, sessionCsrf, sessionGeneration } from '../services/session';

const AuthContext = createContext(null);
let signOutRequest = Promise.resolve();

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const checkAuth = useCallback(async () => {
    Cookies.remove('auth_token'); localStorage.removeItem('auth_token');
    const generation = sessionGeneration();
    try {
      const response = await api.get('/api/auth/web/session');
      if (generation !== sessionGeneration()) return;
      changeSession(response.data.csrfToken, true);
      setUser(response.data.user);
      setLoading(false);
    } catch (err) {
      if (generation !== sessionGeneration() || err.name === 'CanceledError') return;
      Cookies.remove('auth_token');
      localStorage.removeItem('auth_token');
      changeSession(); resetPulseState();
      setUser(null);
      setLoading(false);
    } finally {
      if (generation === sessionGeneration()) setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
    const clear = () => { setUser(null); setLoading(false); };
    const storage = event => { if (event.key === 'assetflow-signout') { changeSession(); resetPulseState(); clear(); } };
    window.addEventListener('assetflow:auth-cleared', clear);
    window.addEventListener('storage', storage);
    return () => { window.removeEventListener('assetflow:auth-cleared', clear); window.removeEventListener('storage', storage); };
  }, [checkAuth]);

  const login = async (email, password) => {
    try {
      await signOutRequest;
      setError(null);
      changeSession(); resetPulseState();
      const response = await api.post('/api/auth/web/login', { email, password });
      const { csrfToken, user: userData } = response.data;
      Cookies.remove('auth_token'); localStorage.removeItem('auth_token');
      changeSession(csrfToken, true);
      window.dispatchEvent(new Event('assetflow:auth-ready'));
      setUser(userData);
      setLoading(false);
      return { success: true };
    } catch (err) {
      const message = err.response?.data?.detail || err.response?.data?.message || 'Login failed';
      setError(message);
      return { success: false, error: message };
    }
  };

  const logout = useCallback(async () => {
    const csrfToken = sessionCsrf();
    announceSignOut(); setUser(null); resetPulseState();
    Cookies.remove('auth_token'); localStorage.removeItem('auth_token');
    try { signOutRequest = api.post('/api/auth/web/logout', {}, { timeout: 5000, headers: { 'X-CSRF-Token': csrfToken } }).catch(() => {}); await signOutRequest; }
    catch { /* Local sign-out must still work while the server is unreachable. */ }
  }, []);

  const updateUser = useCallback((userData) => {
    setUser(prev => ({ ...prev, ...userData }));
  }, []);

  const updateUserPreferences = useCallback(async (preferences) => {
    const response = await usersAPI.updatePreferences(preferences);
    setUser(prev => prev ? ({
      ...prev,
      preferences: {
        ...(prev.preferences || {}),
        ...(response.data || {}),
      }
    }) : prev);
    return response.data;
  }, []);

  const value = {
    user,
    loading,
    error,
    login,
    logout,
    updateUser,
    updateUserPreferences,
    isAuthenticated: !!user,
    // 3-role system: SUPER_ADMIN, ADMIN, USER
    isAdmin: user?.role === 'SUPER_ADMIN' || user?.role === 'ADMIN',
    isSuperAdmin: user?.role === 'SUPER_ADMIN',
    isReadOnly: user?.role === 'USER', // USER is view-only
    canWrite: user?.role === 'SUPER_ADMIN' || user?.role === 'ADMIN', // Can add/edit/delete
    canAccessSettings: user?.role === 'SUPER_ADMIN', // Only Super Admin
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
